'use client';

import React, { useState, useCallback, useId } from 'react';
import './Stepper.css';

export interface StepItem {
  /** Unique identifier for the step */
  id: string;
  /** Step label text */
  label: React.ReactNode;
  /** Optional description shown below the label */
  description?: React.ReactNode;
  /** Whether the step is disabled and cannot be navigated to */
  disabled?: boolean;
  /** Optional icon to display instead of the step number */
  icon?: React.ReactNode;
}

export interface StepperProps {
  /** Array of step definitions */
  steps: StepItem[];
  /** Controlled: currently active step ID */
  currentStep?: string;
  /** Uncontrolled: initial step ID (defaults to first step) */
  defaultStep?: string;
  /** Called when user clicks a navigable step */
  onStepChange?: (stepId: string) => void;
  /** Visual variant */
  variant?: 'default' | 'compact' | 'vertical';
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Whether to show checkmarks on completed steps (default: true) */
  showCheckmarks?: boolean;
  /** Whether to show step numbers (default: true) */
  showNumbers?: boolean;
  /** Whether completed steps are clickable (default: false) */
  completedClickable?: boolean;
  /** Custom class for container */
  className?: string;
  /** Accessible label for the stepper */
  'aria-label'?: string;
}

type StepStatus = 'completed' | 'current' | 'pending';

/**
 * Stepper component for displaying progress through a multi-step process.
 * Supports both controlled and uncontrolled modes.
 */
export const Stepper: React.FC<StepperProps> = ({
  steps,
  currentStep,
  defaultStep,
  onStepChange,
  variant = 'default',
  size = 'md',
  showCheckmarks = true,
  showNumbers = true,
  completedClickable = false,
  className = '',
  'aria-label': ariaLabel = 'Progress',
}) => {
  const generatedId = useId();
  const baseId = `hi-ui-stepper-${generatedId}`;

  // Uncontrolled state - defaults to first step
  const [internalStep, setInternalStep] = useState(() => defaultStep ?? steps[0]?.id);

  // Determine if controlled
  const isControlled = currentStep !== undefined;
  const activeStepId = isControlled ? currentStep : internalStep;

  // Find the index of the active step
  const activeStepIndex = steps.findIndex((step) => step.id === activeStepId);

  // Determine step status based on position relative to current step
  const getStepStatus = useCallback(
    (index: number): StepStatus => {
      if (index < activeStepIndex) return 'completed';
      if (index === activeStepIndex) return 'current';
      return 'pending';
    },
    [activeStepIndex]
  );

  // Handle step click
  const handleStepClick = useCallback(
    (stepId: string, index: number, disabled?: boolean) => {
      const status = getStepStatus(index);

      // Only allow clicking completed steps if completedClickable is true
      if (status !== 'completed' || !completedClickable || disabled) {
        return;
      }

      if (!isControlled) {
        setInternalStep(stepId);
      }
      onStepChange?.(stepId);
    },
    [getStepStatus, completedClickable, isControlled, onStepChange]
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, stepId: string, index: number, disabled?: boolean) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleStepClick(stepId, index, disabled);
      }
    },
    [handleStepClick]
  );

  // Handle empty steps array
  if (!steps.length) {
    return null;
  }

  const containerClasses = [
    'hi-ui',
    'hi-ui-stepper',
    `hi-ui-stepper--${variant}`,
    `hi-ui-stepper--${size}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Render the indicator content (number, checkmark, or icon)
  const renderIndicator = (step: StepItem, index: number, status: StepStatus) => {
    // Custom icon takes precedence
    if (step.icon) {
      return <span className="hi-ui-stepper__icon">{step.icon}</span>;
    }

    // Completed steps show checkmark if enabled
    if (status === 'completed' && showCheckmarks) {
      return (
        <svg
          className="hi-ui-stepper__checkmark"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      );
    }

    // Show step number
    if (showNumbers) {
      return <span className="hi-ui-stepper__number">{index + 1}</span>;
    }

    // Fallback to dot if no number or checkmark
    return <span className="hi-ui-stepper__dot" aria-hidden="true" />;
  };

  return (
    <nav className={containerClasses} aria-label={ariaLabel} data-testid="stepper">
      <ol className="hi-ui-stepper__list" role="list">
        {steps.map((step, index) => {
          const status = getStepStatus(index);
          const isClickable = status === 'completed' && completedClickable && !step.disabled;

          const itemClasses = [
            'hi-ui-stepper__item',
            `hi-ui-stepper__item--${status}`,
            step.disabled && 'hi-ui-stepper__item--disabled',
          ]
            .filter(Boolean)
            .join(' ');

          const indicatorClasses = [
            'hi-ui-stepper__indicator',
            isClickable && 'hi-ui-stepper__indicator--clickable',
          ]
            .filter(Boolean)
            .join(' ');

          // Screen reader text for step status
          const srStatus =
            status === 'completed' ? '(completed)' : status === 'current' ? '(current)' : '';
          const srText = `Step ${index + 1} of ${steps.length}: ${typeof step.label === 'string' ? step.label : ''} ${srStatus}`;

          return (
            <li
              key={step.id}
              className={itemClasses}
              aria-current={status === 'current' ? 'step' : undefined}
            >
              {/* Connector line (before all except first) */}
              {index > 0 && (
                <div
                  className={`hi-ui-stepper__connector hi-ui-stepper__connector--${status === 'pending' ? 'pending' : 'completed'}`}
                  aria-hidden="true"
                />
              )}

              {/* Step indicator (circle with number/icon/checkmark) */}
              <div
                id={`${baseId}-step-${step.id}`}
                className={indicatorClasses}
                role={isClickable ? 'button' : undefined}
                tabIndex={isClickable ? 0 : undefined}
                aria-disabled={step.disabled || undefined}
                onClick={() => handleStepClick(step.id, index, step.disabled)}
                onKeyDown={(e) => handleKeyDown(e, step.id, index, step.disabled)}
              >
                {renderIndicator(step, index, status)}
                <span className="hi-ui-sr-only">{srText}</span>
              </div>

              {/* Step label and description */}
              <div className="hi-ui-stepper__content">
                <span className="hi-ui-stepper__label">{step.label}</span>
                {step.description && (
                  <span className="hi-ui-stepper__description">{step.description}</span>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
